import { useRouter } from 'next/router'
import React, { useMemo, useState } from 'react'

import { Button } from '@/components/Button/Button'
import { Icon } from '@/components/Icon/Icon'
import { useCart } from '@/shared/cart/CartContext'
import { StyledBreadcrumbLink } from '@/shared/components/Breadcrumbs/BreadcrumbLink'
import { Breadcrumbs } from '@/shared/components/Breadcrumbs/Breadcrumbs'
import { CustomImage } from '@/shared/components/CustomImage/CustomImage'
import { ImageLightbox } from '@/shared/components/ProductCarousel/ImageLightbox/ImageLightbox'
import { ProductCarousel } from '@/shared/components/ProductCarousel/ProductCarousel'
import { formatUsd } from '@/shared/libs/money'
import { pxToRem } from '@/shared/styles/themes'
import { faArrowRight } from '@fortawesome/pro-light-svg-icons/faArrowRight'
import { faCheck } from '@fortawesome/pro-light-svg-icons/faCheck'
import { faShoppingCart } from '@fortawesome/pro-light-svg-icons/faShoppingCart'
import { Box, Container, Divider, Grid, Skeleton, styled, Typography, useMediaQuery, useTheme } from '@mui/material'

import { BookCoverType, hasCompareAtDiscount, selectBookByCoverType, useShopCatalog } from './useShopCatalog'

interface BookProductPageProps {
  coverType: BookCoverType
  title: string
  images: string[]
  description: string
  features: string[]
}

export const BookProductPage: React.FC<BookProductPageProps> = ({
  coverType,
  title,
  images: imageUrls,
  description,
  features,
}) => {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  const router = useRouter()

  const { data: catalog, isLoading } = useShopCatalog()
  const book = selectBookByCoverType(catalog, coverType)
  const { addItem, buyNow, hasItem, isCheckingOut } = useCart()

  const [lightboxOpen, setLightboxOpen] = useState(false)
  const [lightboxIndex, setLightboxIndex] = useState(0)

  const images = useMemo(
    () => imageUrls.map((url) => ({ url, alt: title })),
    [imageUrls, title],
  )

  const lightboxImages = useMemo(
    () => images.map(({ url, alt }) => ({ src: url, loading: 'lazy' as const, alt })),
    [images],
  )

  const isBookInCart = book ? hasItem({ kind: 'physical_book', preset_id: book.preset_id }) : false

  const onImageClick = (index?: number) => {
    setLightboxIndex(index || 0)
    setTimeout(() => {
      setLightboxOpen(true)
    }, 10)
  }

  const onLightBoxClose = () => {
    setLightboxOpen(false)
  }

  const handleAddToCart = () => {
    if (!book) return

    addItem({ kind: 'physical_book', preset_id: book.preset_id }, { goToCart: true })
  }

  const handleViewCart = () => {
    router.push('/cart')
  }

  const handleBuyNow = async () => {
    if (!book) return

    await buyNow([{ kind: 'physical_book', preset_id: book.preset_id, quantity: 1 }])
  }

  if (isLoading) {
    return (
      <Container maxWidth="lg">
        <StyledPageWrapper>
          <Grid container spacing={4}>
            <Grid item xs={12} md={7}>
              <Skeleton variant="rounded" height={isMobile ? 300 : 500} />
            </Grid>
            <Grid item xs={12} md={5}>
              <Skeleton variant="text" width="60%" height={40} />
              <Skeleton variant="text" width="40%" height={48} sx={{ mt: 1 }} />
              <Skeleton variant="rounded" height={1} sx={{ my: 3 }} />
              {[1, 2, 3].map((i) => (
                <Skeleton key={i} variant="text" width="70%" height={28} sx={{ mb: 1 }} />
              ))}
              <Skeleton variant="rounded" height={48} sx={{ mt: 4 }} />
              <Skeleton variant="rounded" height={48} sx={{ mt: 2 }} />
            </Grid>
          </Grid>
        </StyledPageWrapper>
      </Container>
    )
  }

  return (
    <Container maxWidth="lg">
      <StyledPageWrapper>
        <Breadcrumbs sx={{ mb: 3 }}>
          <StyledBreadcrumbLink href="/shop">Shop</StyledBreadcrumbLink>
          <Typography variant="body2" color="text.primary" fontWeight={500}>
            {title}
          </Typography>
        </Breadcrumbs>

        <Grid container spacing={4}>
          {/* Left column — Images */}
          <Grid item xs={12} md={7}>
            <StyledCarouselWrapper>
              <ProductCarousel wrapper>
                {!isMobile && (
                  <ProductCarousel imagelist direction="column" spacing={pxToRem(10)}>
                    {images.map((img, index) => (
                      <ProductCarousel thumb key={index} index={index}>
                        <CustomImage src={img.url} alt={img.alt} objectPosition="50% 50%" />
                      </ProductCarousel>
                    ))}
                  </ProductCarousel>
                )}
                <ProductCarousel slider>
                  {images.map((img, index) => (
                    <ProductCarousel
                      slide
                      onImageClick={onImageClick}
                      src={img.url}
                      alt={img.alt}
                      fill
                      style={{ objectFit: 'contain' }}
                      key={index}
                      index={index}
                    />
                  ))}
                </ProductCarousel>
                {isMobile && (
                  <ProductCarousel imagelist direction="row">
                    {images.map((img, index) => (
                      <ProductCarousel thumb key={index} index={index}>
                        <CustomImage src={img.url} alt={img.alt} objectPosition="50% 50%" />
                      </ProductCarousel>
                    ))}
                  </ProductCarousel>
                )}
                <ImageLightbox
                  open={lightboxOpen}
                  setCurrentIndex={setLightboxIndex}
                  currentImageIndex={lightboxIndex}
                  onClose={onLightBoxClose}
                  images={lightboxImages}
                />
              </ProductCarousel>
            </StyledCarouselWrapper>
          </Grid>

          {/* Right column — Details */}
          <Grid item xs={12} md={5}>
            <StyledDetailsColumn>
              <Typography variant="h4" component="h1" fontWeight="bold">
                {title}
              </Typography>

              <Typography variant="body1" color="text.secondary" sx={{ mt: 1 }}>
                {description}
              </Typography>

              <StyledPriceRow>
                {book && hasCompareAtDiscount(book.price, book.compare_at_price) && (
                  <StyledStrikethroughPrice variant="h4" fontWeight={400}>
                    {formatUsd(book.compare_at_price)}
                  </StyledStrikethroughPrice>
                )}
                <Typography variant="h3" color="primary" fontWeight="bold">
                  {book ? formatUsd(book.price) : 'Currently unavailable'}
                </Typography>
              </StyledPriceRow>

              <Divider sx={{ my: 3 }} />

              <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                What&apos;s included
              </Typography>
              <StyledFeatureList>
                {features.map((feature) => (
                  <StyledFeatureItem key={feature}>
                    <Icon icon={faCheck} />
                    <Typography variant="body1">{feature}</Typography>
                  </StyledFeatureItem>
                ))}
              </StyledFeatureList>

              <StyledButtonGroup>
                {!book ? (
                  <Button
                    outline
                    size="large"
                    fullWidth
                    disabled
                  >
                    Currently Unavailable
                  </Button>
                ) : isBookInCart ? (
                  <Button
                    outline
                    size="large"
                    fullWidth
                    onClick={handleViewCart}
                  >
                    View Cart
                    <Icon icon={faShoppingCart} style={{ marginLeft: 8 }} />
                  </Button>
                ) : (
                  <>
                    <Button
                      primary
                      size="large"
                      fullWidth
                      onClick={handleAddToCart}
                      disabled={isCheckingOut}
                    >
                      Add to Cart
                      <Icon icon={faShoppingCart} style={{ marginLeft: 8 }} />
                    </Button>
                    <Button
                      outline
                      size="large"
                      fullWidth
                      onClick={handleBuyNow}
                      disabled={isCheckingOut}
                    >
                      {isCheckingOut ? 'Processing...' : 'Buy Now'}
                      <Icon icon={faArrowRight} style={{ marginLeft: 8 }} />
                    </Button>
                  </>
                )}
              </StyledButtonGroup>

            </StyledDetailsColumn>
          </Grid>
        </Grid>
      </StyledPageWrapper>
    </Container>
  )
}

const StyledCarouselWrapper = styled(Box)(({ theme }) => ({
  '& > .MuiGrid-root': {
    [theme.breakpoints.up('md')]: {
      overflowX: 'hidden',
      flexWrap: 'nowrap',
    },
  },
  '& > .MuiGrid-root > div:has(.carousel)': {
    [theme.breakpoints.up('sm')]: {
      width: '100%',
      maxWidth: 'none',
      flex: '1 1 0',
      minWidth: 0,
    },
  },
}))

const StyledPageWrapper = styled(Box)`
  padding: ${pxToRem(32)} 0 ${pxToRem(64)};
`

const StyledDetailsColumn = styled(Box)`
  position: sticky;
  top: ${pxToRem(24)};
`

const StyledPriceRow = styled(Box)`
  display: flex;
  align-items: baseline;
  gap: ${pxToRem(12)};
  margin-top: ${pxToRem(12)};
`

const StyledStrikethroughPrice = styled(Typography)`
  text-decoration: line-through;
  color: ${(props) => props.theme.palette.text.disabled};
`

const StyledFeatureList = styled(Box)`
  display: flex;
  flex-direction: column;
  gap: ${pxToRem(10)};
  margin-top: ${pxToRem(8)};
`

const StyledFeatureItem = styled(Box)`
  display: flex;
  align-items: center;
  gap: ${pxToRem(10)};
  color: ${(props) => props.theme.palette.success.main};

  & > p {
    color: ${(props) => props.theme.palette.text.primary};
  }
`

const StyledButtonGroup = styled(Box)`
  display: flex;
  flex-direction: column;
  gap: ${pxToRem(12)};
  margin-top: ${pxToRem(28)};
`
